// @ts-nocheck
import { useEffect, useMemo, useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "./popover";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "./accordion";
import { ListFilter, Search, X } from "lucide-react";
import { cn } from "../../lib/utils";
import { Badge } from "./badge";
import { Input } from "./input";
import { Dropdown } from "./dropdown";
import { AgentDropdown } from "./agent-dropdown";
import { useCampaignNames, useVoices } from "../../features/home/queries";
import { listWorkflows } from "../../features/workflows/api";
import { core } from "../../lib/api";
import { useQuery } from "@tanstack/react-query";

export interface FilterState {
  agent_id?: string;
  /** Resolved display name for the selected agent — this is the value the
   *  analytics API expects for its `agent_name` query param. */
  agent_name?: string;
  version_name?: string;
  workflow_id?: string;
  status?: string;
  direction?: string;
  search?: string;
  search_field?: "all" | "call_id" | "contact_id" | "schedule_id" | "phone" | "agent";
  campaign?: string;
  voice?: string;
  direction?: string;
  duration_operator?: string;
  max_duration?: string;
  classification?: string;
  /** Which call_workflows graph placed the call, and which of its agents
   *  dialed it — separate from agent_id/workflow_id above (that pair drives
   *  the combined Agent dropdown; these drive the dedicated Workflow /
   *  Dialed By controls below). */
  dial_workflow_id?: string;
  dial_agent_name?: string;
  /** A WhatsApp/Email/SMS a workflow sent to the call's contact — same join
   *  as the CRM Activity log / Leads page "Sent Via" filter. */
  sent_channel?: string;
}

interface FilterPopoverProps {
  value: FilterState;
  onChange: (value: FilterState) => void;
  className?: string;
  variant?: "calls" | "callbacks" | "batches";
}

export function FilterPopover({ value, onChange, className, variant = "calls" }: FilterPopoverProps) {
  const [open, setOpen] = useState(false);
  const campaigns = useCampaignNames();
  const voices = useVoices();
  const [versions, setVersions] = useState<Array<{ version_id: string; version_name?: string }>>([]);
  const [versionsLoading, setVersionsLoading] = useState(false);

  const workflows = useQuery({
    queryKey: ["call-workflows", "published"],
    queryFn: () => listWorkflows("published"),
    staleTime: 300_000,
  });
  const selectedDialWorkflow = (workflows.data || []).find((w: any) => w.id === value.dial_workflow_id);
  const dialAgentOptions = useMemo(() => {
    const nodes = selectedDialWorkflow?.nodes || [];
    return Array.from(new Set(nodes.map((n: any) => n.agent_name).filter(Boolean))) as string[];
  }, [selectedDialWorkflow]);

  useEffect(() => {
    const agentId = value.agent_id;
    if (!agentId) {
      setVersions([]);
      return;
    }

    let cancelled = false;
    setVersionsLoading(true);
    core<Array<{ version_id: string; version_name?: string }>>(
      `/api/v1/agents/${encodeURIComponent(agentId)}/versions/summary`,
    )
      .then((items) => {
        if (!cancelled) setVersions(Array.isArray(items) ? items : []);
      })
      .catch(() => {
        if (!cancelled) setVersions([]);
      })
      .finally(() => {
        if (!cancelled) setVersionsLoading(false);
      });

    return () => { cancelled = true; };
  }, [value.agent_id]);

  const activeCount = useMemo(() => {
    let count = 0;
    if (value.agent_id || value.workflow_id) count++;
    if (value.version_name) count++;
    if (value.status && value.status !== "all") count++;
    if (value.search) count++;
    if (value.campaign) count++;
    if (value.voice) count++;
    if (value.max_duration) count++;
    if (value.classification && value.classification !== "all") count++;
    if (value.dial_workflow_id) count++;
    if (value.dial_agent_name) count++;
    if (value.sent_channel) count++;
    return count;
  }, [value]);

  const update = (key: keyof FilterState, val: any) => {
    onChange({ ...value, [key]: val });
  };

  const clear = () => onChange({});

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          className={cn(
            "flex h-10 items-center gap-2 rounded-xl border border-line bg-elevated px-3 text-sm font-medium transition-colors hover:bg-soft",
            activeCount > 0 && "border-brand-soft bg-brand-soft/30 text-brand hover:bg-brand-soft/50",
            className
          )}
        >
          <ListFilter className="h-4 w-4" />
          <span>Filter</span>
          {activeCount > 0 && (
            <Badge tone="brand" className="ml-1 px-1.5 py-0.5 text-[10px]">
              {activeCount}
            </Badge>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0 bg-elevated border-line shadow-xl rounded-xl" align="start">
        <div className="flex items-center justify-between border-b border-line px-4 py-3">
          <span className="font-semibold text-ink">Filters</span>
          {activeCount > 0 && (
            <button onClick={clear} className="text-xs font-medium text-brand hover:underline">
              Clear all
            </button>
          )}
        </div>
        
        <div className="max-h-[400px] overflow-y-auto p-2">
          <Accordion type="multiple" defaultValue={["base", "identifiers", "metadata"]}>
            <AccordionItem value="base" className="border-none">
              <AccordionTrigger className="px-2 py-2 text-sm font-semibold text-ink hover:bg-soft hover:no-underline rounded-md">
                Base
              </AccordionTrigger>
              <AccordionContent className="px-2 pb-2">
                <div className="space-y-4 pt-1">
                  
                  {/* Agent Filter */}
                  {(variant === "calls" || variant === "batches") && (
                    <div className="space-y-1.5">
                      <label className="text-xs font-medium text-ink-2">Agent</label>
                      <AgentDropdown
                        value={{ agent_id: value.agent_id, workflow_id: value.workflow_id }}
                        onChange={(val) => onChange({ ...value, agent_id: val.agent_id, agent_name: val.agent_name, workflow_id: val.workflow_id, version_name: undefined })}
                      />
                    </div>
                  )}

                  {variant === "calls" && (
                    <div className="space-y-1.5">
                      <label className="text-xs font-medium text-ink-2">Version</label>
                      <Dropdown
                        ariaLabel="Select Version"
                        value={value.version_name || ""}
                        disabled={!value.agent_id || versionsLoading}
                        options={[
                          { value: "", label: value.agent_id ? (versionsLoading ? "Loading versions..." : "All versions") : "Select an agent first" },
                          ...versions.map((version) => ({
                            value: version.version_name || version.version_id,
                            label: version.version_name
                              ? `${version.version_name} (${version.version_id})`
                              : version.version_id,
                          })),
                        ]}
                        onChange={(selected) => update("version_name", selected || undefined)}
                      />
                    </div>
                  )}

                  {variant === "calls" && (
                    <div className="space-y-1.5">
                      <label className="text-xs font-medium text-ink-2">Workflow</label>
                      <Dropdown
                        ariaLabel="Select Workflow"
                        searchable
                        value={value.dial_workflow_id || ""}
                        options={[
                          { value: "", label: "Any Workflow" },
                          ...(workflows.data || []).map((w: any) => ({ value: w.id, label: w.name || w.id })),
                        ]}
                        onChange={(v) => onChange({ ...value, dial_workflow_id: v || undefined, dial_agent_name: undefined })}
                      />
                    </div>
                  )}

                  {variant === "calls" && value.dial_workflow_id && (
                    <div className="space-y-1.5">
                      <label className="text-xs font-medium text-ink-2">Dialed By (Agent)</label>
                      <Dropdown
                        ariaLabel="Select Dialed By Agent"
                        searchable
                        value={value.dial_agent_name || ""}
                        options={[
                          { value: "", label: "Any Agent" },
                          ...dialAgentOptions.map((name) => ({ value: name, label: name })),
                        ]}
                        onChange={(v) => update("dial_agent_name", v || undefined)}
                      />
                    </div>
                  )}

                  {variant === "calls" && (
                    <div className="space-y-1.5">
                      <label className="text-xs font-medium text-ink-2">Sent Via</label>
                      <Dropdown
                        ariaLabel="Select Sent Via Channel"
                        value={value.sent_channel || ""}
                        options={[
                          { value: "", label: "Any Channel" },
                          { value: "whatsapp", label: "WhatsApp" },
                          { value: "email", label: "Email" },
                          { value: "sms", label: "SMS" },
                        ]}
                        onChange={(v) => update("sent_channel", v || undefined)}
                      />
                    </div>
                  )}

                  {/* Classification Filter (Callbacks only) */}
                  {variant === "callbacks" && (
                    <div className="space-y-1.5">
                      <label className="text-xs font-medium text-ink-2">Classification</label>
                      <Dropdown
                        ariaLabel="Select Classification"
                        value={value.classification || "all"}
                        options={[
                          { value: "all", label: "Any Classification" },
                          { value: "needs_callback", label: "Needs Callback" },
                          { value: "not_picked", label: "Not Picked" },
                          { value: "voicemail", label: "Voicemail" },
                          { value: "rescheduled", label: "Rescheduled" },
                        ]}
                        onChange={(v) => update("classification", v === "all" ? undefined : v)}
                      />
                    </div>
                  )}

                  {/* Status Filter */}
                  <div className="space-y-1.5">
                    <label className="text-xs font-medium text-ink-2">Status</label>
                    <Dropdown
                      ariaLabel="Select Status"
                      value={value.status || "all"}
                      options={variant === "calls" ? [
                        { value: "all", label: "Any Status" },
                        { value: "completed", label: "Completed" },
                        { value: "failed", label: "Failed" },
                        { value: "voicemail", label: "Voicemail" }
                      ] : variant === "batches" ? [
                        { value: "all", label: "Any Status" },
                        { value: "draft", label: "Draft" },
                        { value: "paused", label: "Paused" },
                        { value: "running", label: "Dialing" },
                        { value: "completed", label: "Completed" },
                      ] : [
                        { value: "all", label: "Any Status" },
                        { value: "pending", label: "Pending" },
                        { value: "scheduled", label: "Scheduled" },
                        { value: "paused", label: "Paused" },
                        { value: "completed", label: "Completed" },
                        { value: "failed", label: "Failed" },
                        { value: "cancelled", label: "Cancelled" },
                        { value: "needs_schedule_details", label: "Needs Schedule Details" },
                      ]}
                      onChange={(v) => update("status", v === "all" ? undefined : v)}
                    />
                  </div>

                  {/* Direction Filter */}
                  {variant === "calls" && (
                    <div className="space-y-1.5">
                      <label className="text-xs font-medium text-ink-2">Direction</label>
                      <Dropdown
                        ariaLabel="Filter by Direction"
                        value={value.direction || "all"}
                        options={[
                          { value: "all", label: "All Directions" },
                          { value: "inbound", label: "Inbound" },
                          { value: "outbound", label: "Outbound" },
                        ]}
                        onChange={(v) => update("direction", v === "all" ? undefined : v)}
                      />
                    </div>
                  )}

                  {/* Duration Filter */}
                  {variant === "calls" && (
                    <div className="space-y-1.5">
                      <label className="text-xs font-medium text-ink-2">Duration (seconds)</label>
                      <div className="flex gap-2">
                        <Dropdown
                          className="w-24"
                          ariaLabel="Duration Operator"
                          value={value.duration_operator || ">="}
                          options={[
                            { value: ">=", label: "≥" },
                            { value: "<=", label: "≤" },
                            { value: "=", label: "=" }
                          ]}
                          onChange={(v) => update("duration_operator", v)}
                        />
                        <Input 
                          className="flex-1 text-ink" 
                          type="number" 
                          placeholder="e.g. 60"
                          value={value.max_duration || ""}
                          onChange={(e) => {
                            const val = e.target.value || undefined;
                            onChange({
                              ...value,
                              max_duration: val,
                              duration_operator: val ? (value.duration_operator || ">=") : value.duration_operator,
                            });
                          }}
                        />
                      </div>
                    </div>
                  )}

                </div>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="identifiers" className="border-none">
              <AccordionTrigger className="px-2 py-2 text-sm font-semibold text-ink hover:bg-soft hover:no-underline rounded-md">
                Identifiers
              </AccordionTrigger>
              <AccordionContent className="px-2 pb-2">
                <div className="space-y-4 pt-1">
                  
                  {/* Search Field & Query */}
                  <div className="space-y-1.5">
                    <div className="flex gap-2">
                      <Dropdown
                        className="w-[120px]"
                        ariaLabel="Search Field"
                        value={value.search_field || (variant === "batches" ? "all" : "contact_id")}
                        options={variant === "batches" ? [
                          { value: "all", label: "All fields" },
                          { value: "contact_id", label: "Batch name" },
                          { value: "agent", label: "Agent" },
                          { value: "schedule_id", label: "Schedule ID" },
                        ] : [
                          { value: "contact_id", label: "Contact ID" },
                          { value: "call_id", label: "Call ID" },
                          { value: "schedule_id", label: "Schedule ID" },
                          { value: "phone", label: "Phone" }
                        ]}
                        onChange={(v) => update("search_field", v)}
                      />
                      <Input 
                        className="flex-1 text-ink" 
                        placeholder="Search..."
                        value={value.search || ""}
                        onChange={(e) => update("search", e.target.value || undefined)}
                      />
                    </div>
                  </div>

                </div>
              </AccordionContent>
            </AccordionItem>

            {/* Metadata (Calls only) */}
            {variant === "calls" && (
              <AccordionItem value="metadata" className="border-none">
                <AccordionTrigger className="px-2 py-2 text-sm font-semibold text-ink hover:bg-soft hover:no-underline rounded-md">
                  Metadata
                </AccordionTrigger>
                <AccordionContent className="px-2 pb-2">
                  <div className="space-y-4 pt-1">
                  
                  <div className="space-y-1.5">
                    <label className="text-xs font-medium text-ink-2">Campaign</label>
                    <Dropdown
                      ariaLabel="Select Campaign"
                      value={value.campaign || ""}
                      options={[
                        { value: "", label: "Any Campaign" },
                        ...(campaigns.data || []).map((c: any) => {
                          const val = typeof c === 'string' ? c : (c.name || c.id || "");
                          return { value: val, label: val };
                        }).filter(o => o.value)
                      ]}
                      onChange={(v) => update("campaign", v || undefined)}
                    />
                  </div>

                  <div className="space-y-1.5">
                    <label className="text-xs font-medium text-ink-2">Voice</label>
                    <Dropdown
                      ariaLabel="Select Voice"
                      value={value.voice || ""}
                      options={[
                        { value: "", label: "Any Voice" },
                        ...(voices.data || []).map((v: any) => {
                          const val = v.local_voice_name || v.id || v.voice_id || "";
                          const label = v.name || v.local_voice_name || v.voice_id || "";
                          return { value: val, label: label };
                        }).filter(o => o.value)
                      ]}
                      onChange={(v) => update("voice", v || undefined)}
                    />
                  </div>

                </div>
              </AccordionContent>
            </AccordionItem>
            )}
          </Accordion>
        </div>
      </PopoverContent>
    </Popover>
  );
}
